/**
 * AuthView — register / sign in inside the embed. Reuses the SAME account
 * system as the WunderGuide apps (email+password + Google SSO):
 *
 *   - register() sends the guest Bearer, so the backend UPGRADES the
 *     anonymous user in place — chat history, itinerary and any queued
 *     "Add to trip" survive under the same user_id.
 *   - Google runs in a POPUP (public/google-sso.html): Google Identity
 *     Services refuses cross-origin iframes, which is what the embed is.
 *     The popup posts the credential back and we exchange it at
 *     /auth/sso/google (guest Bearer attached → same upgrade semantics).
 *
 * Primary entry: useGatedAddItem parked an add and routed here. On success
 * we flush the queue and land on the Trip view.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import config from '../../config/env.json';
import { signInWithGoogle } from '../auth/sso';
import { useInstance } from '../context/InstanceContext';
import { useItinerary } from '../context/ItineraryContext';
import { useView } from '../context/ViewContext';
import { useAuth } from '../hooks/useAuth';
import { claimFirstTouch } from '../api/attributionApi';
import { emit as emitTelemetry } from '../utils/telemetry';
import logger from '../utils/logger';

const GOOGLE_CLIENT_ID =
  ((config as Record<string, unknown>).GOOGLE_CLIENT_ID as string) || '';

type Mode = 'register' | 'signin';

/** Minimal GIS surface (loaded from accounts.google.com/gsi/client). */
interface GsiMoment {
  isNotDisplayed?: () => boolean;
  isSkippedMoment?: () => boolean;
}
type GsiWindow = Window & {
  google?: {
    accounts?: {
      id?: {
        initialize: (cfg: Record<string, unknown>) => void;
        prompt: (cb?: (moment: GsiMoment) => void) => void;
      };
    };
  };
};

const AuthView: React.FC = () => {
  const { t } = useTranslation();
  const { login, register, authState } = useAuth();
  const { effectiveLocale, config: instanceConfig } = useInstance();
  const { pendingAdds, clearPendingAdds } = useItinerary();
  const { setView, goBack } = useView();

  const [mode, setMode] = useState<Mode>('register');
  const [busy, setBusy] = useState(false);
  const [googleBusy, setGoogleBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const popupRef = useRef<Window | null>(null);
  const hasPending = pendingAdds.length > 0;

  // The actual flush of queued adds happens DECLARATIVELY in
  // ItineraryContext once authState is authenticated AND itineraryId is
  // live (an imperative flush here races the post-register bootstrap and
  // drops the POSTs). This just records + navigates — once: the queue
  // clearing after the flush must not re-trigger and yank the view again.
  const finishedRef = useRef(false);
  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    emitTelemetry('auth_completed', {
      props: { pending_adds: pendingAdds.length },
    });
    setView(pendingAdds.length > 0 ? 'itinerary' : 'chat');
  }, [pendingAdds.length, setView]);

  // Already signed in (e.g. Google popup landed, or a second tab upgraded
  // the session) — nothing to collect here.
  useEffect(() => {
    if (authState === 'authenticated') finish();
  }, [authState, finish]);

  // Abandoning the sheet DROPS the queued adds (Michael, 2026-07-02: an add
  // without a registration is a cancelled intent — it must never linger and
  // show up as "added" later). Unmounting via a successful auth keeps the
  // queue so the ItineraryContext flush (which may still be waiting on the
  // account's itinerary bootstrap) can land it.
  //
  // Success is tracked by an EXPLICIT ref set the moment login/register/
  // Google-exchange resolves — NOT by authState: finish() navigates (and
  // unmounts this view) right after the auth call returns, while authState
  // propagates asynchronously (the auth-event listener awaits session info
  // before setState). Racing that propagation cleared the queue before the
  // flush could run — sign-in "worked" but the Trip stayed empty.
  const authSucceededRef = useRef(false);
  const authStateRef = useRef(authState);
  authStateRef.current = authState;
  useEffect(
    () => () => {
      if (!authSucceededRef.current && authStateRef.current !== 'authenticated')
        clearPendingAdds();
    },
    [clearPendingAdds],
  );

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    // Read off the form: iOS Safari autofill bypasses React onChange
    // (same fix as wunderguide-web SignIn).
    const fd = new FormData(e.currentTarget);
    const email = String(fd.get('email') ?? '')
      .trim()
      .toLowerCase();
    const password = String(fd.get('password') ?? '');
    try {
      if (mode === 'register') {
        const first = String(fd.get('first_name') ?? '').trim();
        const last = String(fd.get('last_name') ?? '').trim();
        await register(email, password, first, last, effectiveLocale);
        // First-touch claim with the surface's EFFECTIVE campaign (system
        // campaign, or a blessed host override — resolve already decided).
        claimFirstTouch(
          instanceConfig?.campaign_code,
          instanceConfig?.instance_id,
        );
      } else {
        await login(email, password);
      }
      authSucceededRef.current = true;
      finish();
    } catch (err) {
      const e2 = err as { response?: { data?: { message?: string } } };
      setError(
        e2?.response?.data?.message ||
          t(
            mode === 'register' ? 'auth.register_failed' : 'auth.signin_failed',
          ),
      );
    } finally {
      setBusy(false);
    }
  };

  // Google — SAME flow as app.wunderguide.com: GIS runs IN-WIDGET (custom
  // button → google.accounts.id.prompt()). The bootstrap delegates FedCM
  // into the iframe (allow="identity-credentials-get"), which is what makes
  // the in-iframe chooser possible on partner pages. The exchange also
  // matches the web app: /auth/sso/google with the guest Bearer attached →
  // the guest account (created at init) is upgraded in place.
  const exchange = useCallback(
    async (credential: string) => {
      try {
        setGoogleBusy(true);
        setError(null);
        await signInWithGoogle(credential);
        // Google here is overwhelmingly a sign-UP (guest upgraded in
        // place); existing users are protected server-side by the
        // one-claim-per-user-forever rule.
        claimFirstTouch(
          instanceConfig?.campaign_code,
          instanceConfig?.instance_id,
        );
        authSucceededRef.current = true;
        finish();
      } catch (err) {
        logger.warn('[auth] google exchange failed', err);
        setError(t('auth.google_failed'));
      } finally {
        setGoogleBusy(false);
        popupRef.current = null;
      }
    },
    [finish, t, instanceConfig],
  );

  const gisReadyRef = useRef(false);
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    const init = () => {
      if (gisReadyRef.current) return true;
      const gsi = (window as GsiWindow).google?.accounts?.id;
      if (!gsi) return false;
      gsi.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: (resp: { credential: string }) => exchange(resp.credential),
        use_fedcm_for_prompt: true,
      });
      gisReadyRef.current = true;
      return true;
    };
    if (init()) return;
    if (!document.querySelector('script[src*="gsi/client"]')) {
      const s = document.createElement('script');
      s.src = 'https://accounts.google.com/gsi/client';
      s.async = true;
      document.head.appendChild(s);
    }
    const timer = setInterval(() => {
      if (init()) clearInterval(timer);
    }, 100);
    return () => clearInterval(timer);
  }, [exchange]);

  // Fallback ONLY (no-FedCM browsers): top-level popup running the same
  // GIS prompt; it posts the credential back. Never the primary path.
  useEffect(() => {
    const onMsg = (ev: MessageEvent) => {
      if (ev.origin !== window.location.origin) return;
      const data = ev.data as { type?: string; credential?: string };
      if (data?.type !== 'wg:google_credential' || !data.credential) return;
      exchange(data.credential);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [exchange]);

  const openGoogleFallbackPopup = useCallback(() => {
    const url = `/google-sso.html?client_id=${encodeURIComponent(GOOGLE_CLIENT_ID)}&locale=${encodeURIComponent(effectiveLocale || 'en')}`;
    popupRef.current = window.open(
      url,
      'wg-google-sso',
      'popup,width=420,height=560',
    );
  }, [effectiveLocale]);

  const onGoogleClick = () => {
    if (googleBusy) return;
    setError(null);
    const gsi = (window as GsiWindow).google?.accounts?.id;
    if (!gsi || !gisReadyRef.current) {
      openGoogleFallbackPopup();
      return;
    }
    gsi.prompt((moment: GsiMoment) => {
      // FedCM unavailable / user agent refused the in-iframe chooser →
      // seamless popup fallback (still one Google chooser, our origin).
      if (moment.isNotDisplayed?.() || moment.isSkippedMoment?.()) {
        openGoogleFallbackPopup();
      }
    });
  };

  return (
    <div className="wg-auth">
      <header className="wg-auth__header">
        {/* Auth is PUSHED on top of a flow (a guest tapping add, an agent
            add_to_itinerary) rather than chosen from the nav, so without this
            the visitor is stranded — there is no nav entry for it to leave by.
            goBack() returns to whichever pane pushed it.
            Backing out DROPS any queued adds, via the unmount effect below —
            that is the intended rule (Michael, 2026-07-02: an add without a
            registration is a cancelled intent and must never resurface as
            "added" later), and it is exactly what a Back button should mean. */}
        <button
          type="button"
          className="wg-auth__back"
          onClick={goBack}
          aria-label={t('common.back')}
        >
          <svg
            viewBox="0 0 24 24"
            aria-hidden
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {t('common.back')}
        </button>
        <h2 className="wg-auth__title">
          {hasPending ? t('auth.title_save_trip') : t('auth.title')}
        </h2>
        <p className="wg-auth__sub">
          {hasPending ? t('auth.sub_save_trip') : t('auth.sub')}
        </p>
      </header>

      {GOOGLE_CLIENT_ID && (
        <>
          <button
            type="button"
            className="wg-auth__google"
            onClick={onGoogleClick}
            disabled={googleBusy || busy}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden>
              <path
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                fill="#4285F4"
              />
              <path
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                fill="#34A853"
              />
              <path
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                fill="#FBBC05"
              />
              <path
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                fill="#EA4335"
              />
            </svg>
            {googleBusy ? t('common.loading') : t('auth.continue_google')}
          </button>
          <div className="wg-auth__divider">
            <span>{t('auth.or')}</span>
          </div>
        </>
      )}

      <form className="wg-auth__form" onSubmit={onSubmit}>
        {mode === 'register' && (
          <div className="wg-auth__row">
            <input
              name="first_name"
              autoComplete="given-name"
              placeholder={t('auth.first_name')}
              required
            />
            <input
              name="last_name"
              autoComplete="family-name"
              placeholder={t('auth.last_name')}
              required
            />
          </div>
        )}
        <input
          name="email"
          type="email"
          autoComplete="email"
          placeholder={t('auth.email')}
          required
        />
        <input
          name="password"
          type="password"
          autoComplete={
            mode === 'register' ? 'new-password' : 'current-password'
          }
          placeholder={t('auth.password')}
          minLength={8}
          required
        />
        {error && <div className="wg-auth__error">{error}</div>}
        <button type="submit" className="wg-auth__submit" disabled={busy}>
          {busy
            ? t('common.loading')
            : mode === 'register'
              ? t('auth.create_account')
              : t('auth.sign_in')}
        </button>
      </form>

      <div className="wg-auth__switch">
        {mode === 'register' ? (
          <>
            {t('auth.have_account')}{' '}
            <button type="button" onClick={() => setMode('signin')}>
              {t('auth.sign_in')}
            </button>
          </>
        ) : (
          <>
            {t('auth.no_account')}{' '}
            <button type="button" onClick={() => setMode('register')}>
              {t('auth.create_account')}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default AuthView;
